import React, { useState, useEffect, useCallback, useMemo } from "react";

import { MediaQueryContext } from "../context/MediaQueryContext";

import { breakpoints } from "./breakpoints";
import { MediaQueryInfo, ScreenMode } from "./Responsive.types";

/**
 * Functional component to represent a wrapper that handles media queries and breakpoint updates
 * Exposes breakpoint state using context
 *
 * @param {Object} props - contains child components to render within wrapper
 */
export const ResponsiveWrapper: React.FC<React.PropsWithChildren<any>> = (props) => {
  const mediaQueryState: MediaQueryInfo[] = useMemo(() => [], []);
  const [activeQuery, setActiveQuery] = useState<MediaQueryInfo | null>(null);

  // Handle setting the screen mode based on the currently active breakpoint.
  // This information will be used for conditionally rendering UI elements
  // throughout the application
  const setScreenMode = (query: MediaQueryInfo) => {
    switch (query.breakpoint) {
      case "desktopLg":
      case "desktopMd":
      case "desktopSm":
      case "tabletLg":
      case "tabletMd":
        query.screenMode = ScreenMode.DESKTOP;
        break;
      case "mobileSm":
      case "mobileMd":
      case "mobileLg":
        query.screenMode = ScreenMode.MOBILE;
        break;
    }
  };

  // create the function that will prepare our breakpoint object for dispatch
  const dispatchActiveQuery = useCallback(() => {
    // Assuming a desktop first responsive system, the active
    // breakpoint will always be the smallest one that satisfies
    // the media query. We use reduce to travel down the matched media queries
    // and return the smallest item that matches. In the event no breakpoints
    // match return first query corresponding to largest breakpoint
    const activeQuery: MediaQueryInfo = mediaQueryState.reduce((prev, curr) => {
      return curr.matches
        ? curr
        : prev && prev.matches
        ? prev
        : mediaQueryState[0];
    });
    setScreenMode(activeQuery);
    setActiveQuery(activeQuery);
  }, [mediaQueryState]);

  useEffect(() => {
    // loop over our breakpoints object to create a media query
    // instance for each of our breakpoints
    Object.entries(breakpoints).forEach((entry) => {
      const key: string = entry[0];
      const value: number = entry[1];

      // create a new media query object using the window.matchMedia api.
      // Read more here: https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
      const query: any = window.matchMedia(`(max-width: ${value}px)`);

      query.breakpoint = key;
      query.size = value;

      // Use desktop screen mode as default
      query.screenMode = ScreenMode.DESKTOP;

      // Attach the event listener to the query
      query.addEventListener("change", dispatchActiveQuery);

      // push this query to our mediaQueryState array,
      // after the loops has run its course, we will
      // have an array that houses a mediaQuery object
      // for each breakpoint in our breakpoints object.
      mediaQueryState.push(query as MediaQueryInfo);
    });

    // run the function once on componentDidMount to
    // record the initial breakpoint state of our viewport
    dispatchActiveQuery();
  }, [dispatchActiveQuery, mediaQueryState]);

  return (
    <MediaQueryContext.Provider value={activeQuery}>
      <div>{props.children}</div>
    </MediaQueryContext.Provider>
  );
};
